import { inject, Injectable, signal } from '@angular/core';
import { CreateUserFlowDraft, UiDraft } from '@features/users/interfaces';
import { DraftSubject } from '../draft-subject/draft-subject.service';

/**
 * This service is responsible in performing api calls ONLY related to the draft
 *
 * @remarks
 * Used in the create user module to manage draft operations such as saving to the database
 */
@Injectable({
    providedIn: 'root',
})
export class DraftApiService {
    /**
     * Inject draft subject to listen to emitted new draft to save in the db
     */
    private _draftSubject = inject(DraftSubject);

    /**
     * Whether we're performing an async operation
     */
    private _isLoading = signal(false);

    /**
     * Public isLoading property. Readonly value
     */
    public isLoading = this._isLoading.asReadonly();

    /**
     * Set up autosave when new draft is recieved from the draft subject
     *
     * @remarks
     * This automatically performs api call to save the draft when new and updated draft is emitted.
     * This draft subject only emits draft that is updated on stepper navigation, on window blur, or even on hard refresh.
     * The same draft data does not get emitted to prevent duplicate and redundant api calls
     */
    constructor() {
        this.setupAutosave();
    }

    /**
     * Set is loading when performing async operations
     */
    public setIsLoading(value: boolean) {
        this._isLoading.set(value);
    }

    /**
     * Setup auto save using rxjs Subject so when the user updates the draft, we send and save it to the backend
     */
    private setupAutosave() {
        try {
            this._draftSubject.getDraftSubject().subscribe(({ draft, fn }) => {
                this.saveToDraft(draft, fn);
            });
        } catch (error) {
            console.error('Error setting up autosave: ', error);
        }
    }

    /**
     * Save to draft api call. Temporary only, save to the local storage for now
     */
    public async saveToDraft(data: any, fn?: () => void) {
        this._isLoading.set(true);
        return new Promise((resolve) => {
            setTimeout(() => {
                this.saveToLocaleStorage(data);
                this._isLoading.set(false);
                fn?.(); // Invoke callback function, either increment the step or sub step depends on the caller
                resolve(true);
            }, 1000);
        });
    }

    /**
     * Temporary only - retrieve drafts from local storage
     */
    public async getUserDrafts(): Promise<UiDraft<keyof CreateUserFlowDraft>[]> {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const drafts = localStorage.getItem('draft');
        if (drafts) {
            return JSON.parse(drafts);
        }
        return [];
    }

    /**
     * Save to local storage - Temporary only
     * @param data
     */
    public saveToLocaleStorage(data: any): void {
        // First take old data from local storage
        const oldData = localStorage.getItem('draft');

        if (oldData) {
            const parsedData = JSON.parse(oldData);
            // Assume that parsed data is an array
            if (Array.isArray(parsedData)) {
                // Check if the data already exists in the array
                const existingIndex = parsedData.findIndex((item: any) => item.id === data.id);

                if (existingIndex !== -1) {
                    // Update the existing data in the array
                    data.updatedAt = new Date().toISOString();
                    parsedData[existingIndex] = data;
                } else {
                    // Add the new data
                    parsedData.push(data);
                }

                // Update the old data with the new data
                localStorage.setItem('draft', JSON.stringify(parsedData));
            }
        } else {
            localStorage.setItem('draft', JSON.stringify([data]));
        }
    }
}
